/**
 * Runtime access to the muzone theme.
 *
 * The themes themselves live in `src/styles/themes/` — one Sass map per theme,
 * emitted as CSS custom properties. This module is the JavaScript side of that:
 * it switches the active theme and reads token values for the few places that
 * draw colour from JS rather than CSS (the MapLibre style, the animated app
 * backdrop, inline `style` props).
 *
 * There is no copy of the palette here on purpose. Token values, the list of
 * themes and which one is the base are all read from the live document, so JS
 * and CSS cannot drift apart, and a read after a theme switch returns the new
 * theme's value with no extra bookkeeping.
 */

const THEME_ATTRIBUTE = 'data-theme';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';
export const SYSTEM_THEME_PREFERENCE = 'system';

/**
 * Fallback base theme id, used only when no stylesheet is loaded (SSR, unit
 * tests). The real answer comes from `--mz-theme-base`; this exists so callers
 * still get a sensible id rather than `undefined` when there is no CSS to read.
 */
export const FALLBACK_THEME_ID = 'light';

const root = () =>
    (typeof document === 'undefined' ? null : document.documentElement);

const unquote = (value) => value.replace(/['"]/g, '').trim();

/**
 * The theme that renders when no `data-theme` attribute is set.
 *
 * Published by the theme layer on `:root` and deliberately not redeclared by the
 * per-theme blocks, so it reports the base no matter which theme is active.
 */
export const getBaseThemeId = () =>
    unquote(readToken('--mz-theme-base')) || FALLBACK_THEME_ID;

/**
 * Theme ids the stylesheet actually shipped.
 *
 * Read from `--mz-theme-catalog`, which the registry emits from the same map it
 * builds the themes from — so this list cannot fall out of step with the SCSS
 * the way a hand-maintained array would.
 */
export const listThemeIds = () => {
    const raw = readToken('--mz-theme-catalog');
    if (!raw) {
        return [FALLBACK_THEME_ID];
    }
    return unquote(raw)
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
};

/**
 * The theme currently painting the document.
 *
 * Resolved from `--mz-theme-id` rather than from the attribute, so it reports
 * what the cascade actually settled on — including the base, whose block carries
 * no attribute at all.
 */
export const getActiveThemeId = () =>
    unquote(readToken('--mz-theme-id'))
    || root()?.getAttribute(THEME_ATTRIBUTE)
    || getBaseThemeId();

const systemDarkQuery = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return null;
    }
    try {
        return window.matchMedia(SYSTEM_DARK_QUERY);
    } catch (_) {
        return null;
    }
};

export const getSystemThemeId = () => {
    const themeIds = listThemeIds();
    const baseThemeId = getBaseThemeId();
    const lightThemeId = themeIds.includes('light') ? 'light' : baseThemeId;
    const darkThemeId = themeIds.includes('dark') ? 'dark' : baseThemeId;
    return systemDarkQuery()?.matches ? darkThemeId : lightThemeId;
};

export const resolveThemePreference = (themePreference) => {
    const normalized = typeof themePreference === 'string'
        ? themePreference.trim().toLowerCase()
        : '';
    if (normalized === SYSTEM_THEME_PREFERENCE) {
        return getSystemThemeId();
    }
    return listThemeIds().includes(normalized) ? normalized : getSystemThemeId();
};

/**
 * Apply a theme by id. Returns the id that ended up active.
 *
 * An unregistered id is ignored rather than applied: `data-theme="nope"` matches
 * no rule, so the app would silently keep rendering the base theme while every
 * caller believed the switch worked.
 */
export const applyTheme = (themeId) => {
    const element = root();
    if (!element) {
        return FALLBACK_THEME_ID;
    }
    if (!listThemeIds().includes(themeId)) {
        return getActiveThemeId();
    }
    if (themeId === getBaseThemeId()) {
        element.removeAttribute(THEME_ATTRIBUTE);
    } else {
        element.setAttribute(THEME_ATTRIBUTE, themeId);
    }
    return themeId;
};

export const applyThemePreference = (themePreference) =>
    applyTheme(resolveThemePreference(themePreference));

export const watchSystemThemePreference = (onChange) => {
    const mediaQuery = systemDarkQuery();
    if (!mediaQuery || typeof onChange !== 'function') {
        return () => {};
    }

    const handleChange = () => onChange(getSystemThemeId());
    if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }
    if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
    }
    return () => {};
};

/**
 * Current value of a theme token, e.g. `readToken('--ed-accent')`.
 *
 * Resolved by the browser, so a token defined as `var(--mz-accent)` comes back
 * as the colour it currently paints rather than the reference. Returns an empty
 * string for an unknown token, matching `getPropertyValue`.
 */
export function readToken(name) {
    const element = root();
    if (!element) {
        return '';
    }
    const token = name.startsWith('--') ? name : `--${name}`;
    return getComputedStyle(element).getPropertyValue(token).trim();
}

/**
 * Read several tokens at once, reusing a single style resolution.
 *
 * Worth preferring inside anything that builds a palette in one pass — a map
 * style, a gradient table — since `getComputedStyle` is the expensive part.
 */
export function readTokens(names) {
    const element = root();
    if (!element) {
        return {};
    }
    const computed = getComputedStyle(element);
    return names.reduce((acc, name) => {
        const token = name.startsWith('--') ? name : `--${name}`;
        acc[name] = computed.getPropertyValue(token).trim();
        return acc;
    }, {});
}
